import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import "../styles/auth.css";

/** @typedef {"signin" | "forgot" | "reset"} AuthMode */

export default function SignIn() {
  const {
    login,
    completeNewPassword,
    completeTotpChallenge,
    selectTotpMfa,
    requestPasswordReset,
    confirmPasswordReset,
    isAuthenticated,
    challenge,
    totpSetup,
    loading,
  } = useAuth();
  const navigate = useNavigate();
  /** @type {[AuthMode, function]} */
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (challenge === "MFA_SELECT") {
      selectTotpMfa().catch((err) => {
        setError(err?.message || "Could not select authenticator MFA.");
      });
    }
  }, [challenge, selectTotpMfa]);

  if (!loading && isAuthenticated && !challenge) {
    return <Navigate to="/leads" replace />;
  }

  function mapAuthError(err) {
    const name = err?.name || "";
    if (name === "NotAuthorizedException" || name === "UserNotFoundException") {
      return "Invalid email or password.";
    }
    if (name === "LimitExceededException" || name === "TooManyRequestsException") {
      return "Too many attempts. Try again in a few minutes.";
    }
    if (name === "CodeMismatchException" || name === "ExpiredCodeException") {
      return "Invalid or expired code. Try again.";
    }
    if (name === "EnableSoftwareTokenMFAException") {
      return "Could not verify authenticator code. Check the time on your device and try a fresh code.";
    }
    if (name === "InvalidPasswordException") {
      return "Password does not meet requirements (min 8 characters).";
    }
    if (name === "InvalidParameterException") {
      return err?.message || "Check your email and try again.";
    }
    return err?.message || "Something went wrong.";
  }

  async function finishIfDone(result) {
    if (result?.ok) {
      navigate("/leads", { replace: true });
      return true;
    }
    setTotpCode("");
    return false;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setInfo("");
    setSubmitting(true);
    try {
      if (challenge === "NEW_PASSWORD_REQUIRED") {
        const result = await completeNewPassword(newPassword);
        await finishIfDone(result);
        return;
      }

      if (challenge === "TOTP_CODE" || challenge === "TOTP_SETUP") {
        const result = await completeTotpChallenge(totpCode);
        await finishIfDone(result);
        return;
      }

      if (mode === "forgot") {
        await requestPasswordReset(email);
        setInfo(
          "If that account exists, a reset code was sent to the email. Enter it below."
        );
        setMode("reset");
        setCode("");
        setNewPassword("");
        setConfirmPassword("");
        return;
      }

      if (mode === "reset") {
        if (newPassword !== confirmPassword) {
          setError("Passwords do not match.");
          return;
        }
        await confirmPasswordReset(email, code, newPassword);
        setInfo("Password updated. Sign in with your new password.");
        setMode("signin");
        setPassword("");
        setCode("");
        setNewPassword("");
        setConfirmPassword("");
        return;
      }

      const result = await login(email, password);
      await finishIfDone(result);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const needsNewPassword = challenge === "NEW_PASSWORD_REQUIRED";
  const needsTotp =
    challenge === "TOTP_CODE" || challenge === "TOTP_SETUP";
  const blockingChallenge = needsNewPassword || needsTotp || challenge === "MFA_SELECT";

  let title = "Sign in";
  if (needsNewPassword) title = "Set your password";
  else if (challenge === "TOTP_SETUP") title = "Set up authenticator";
  else if (challenge === "TOTP_CODE") title = "Authenticator code";
  else if (mode === "forgot") title = "Reset password";
  else if (mode === "reset") title = "Enter reset code";

  return (
    <main className="auth-page">
      <div className="auth-card">
        <p className="auth-card__brand">Freight Train of Justice</p>
        <h1 className="auth-card__title">{title}</h1>

        <form className="auth-form" onSubmit={onSubmit}>
          {error ? <p className="auth-error">{error}</p> : null}
          {info ? <p className="auth-info">{info}</p> : null}

          {needsNewPassword ? (
            <label>
              Choose a new password
              <input
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={8}
                required
              />
            </label>
          ) : null}

          {challenge === "TOTP_SETUP" ? (
            <div className="auth-totp-setup">
              <p className="auth-info">
                Add this account in your authenticator app (1Password, Google
                Authenticator, Authy), then enter the 6-digit code.
              </p>
              {totpSetup?.setupUri ? (
                <p className="auth-hint">
                  <a href={totpSetup.setupUri}>Open authenticator setup link</a>
                </p>
              ) : null}
              {totpSetup?.sharedSecret ? (
                <label>
                  Manual key
                  <input
                    type="text"
                    readOnly
                    value={totpSetup.sharedSecret}
                    onFocus={(e) => e.target.select()}
                  />
                </label>
              ) : null}
              <label>
                Authenticator code
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  autoFocus
                />
              </label>
            </div>
          ) : null}

          {challenge === "TOTP_CODE" ? (
            <label>
              Authenticator code
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
              />
            </label>
          ) : null}

          {challenge === "MFA_SELECT" ? (
            <p className="auth-info">Selecting authenticator MFA…</p>
          ) : null}

          {!blockingChallenge && mode === "signin" ? (
            <>
              <label>
                Email
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>
            </>
          ) : null}

          {!blockingChallenge && mode === "forgot" ? (
            <label>
              Email
              <input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
          ) : null}

          {!blockingChallenge && mode === "reset" ? (
            <>
              <label>
                Email
                <input
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
              <label>
                Reset code
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </label>
              <label>
                New password
                <input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </label>
              <label>
                Confirm new password
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </label>
            </>
          ) : null}

          {challenge !== "MFA_SELECT" ? (
            <button className="btn btn--primary" type="submit" disabled={submitting}>
              {submitting
                ? "Working…"
                : needsNewPassword
                  ? "Save & continue"
                  : needsTotp
                    ? "Verify code"
                    : mode === "forgot"
                      ? "Send reset code"
                      : mode === "reset"
                        ? "Update password"
                        : "Sign in"}
            </button>
          ) : null}
        </form>

        {!blockingChallenge ? (
          <div className="auth-links">
            {mode === "signin" ? (
              <button
                className="auth-link-btn"
                type="button"
                onClick={() => {
                  setError("");
                  setInfo("");
                  setMode("forgot");
                }}
              >
                Forgot password?
              </button>
            ) : null}
            {mode === "forgot" || mode === "reset" ? (
              <>
                {mode === "reset" ? (
                  <button
                    className="auth-link-btn"
                    type="button"
                    disabled={submitting}
                    onClick={async () => {
                      setError("");
                      setInfo("");
                      setSubmitting(true);
                      try {
                        await requestPasswordReset(email);
                        setInfo("A new reset code was sent if the account exists.");
                      } catch (err) {
                        setError(mapAuthError(err));
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  >
                    Resend code
                  </button>
                ) : null}
                <button
                  className="auth-link-btn"
                  type="button"
                  onClick={() => {
                    setError("");
                    setInfo("");
                    setMode("signin");
                    setCode("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  ← Back to sign in
                </button>
              </>
            ) : null}
          </div>
        ) : null}

        <p className="auth-hint">
          Invite-only. MFA is optional — enable it under Security after sign-in.
          Password reset does not need admin approval.
        </p>
        <Link className="auth-back" to="/">
          ← Back to home
        </Link>
      </div>
    </main>
  );
}
